import { X } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';

import { Dialog, DialogDescription, DialogTitle } from './dialog';
import { iconButtonClass } from './formClasses';

const SIZE_CLASS = { sm: 'max-w-sm', md: 'max-w-md', lg: 'max-w-xl', xl: 'max-w-3xl' };

type ModalProps = {
  title: ReactNode;
  description?: ReactNode;
  onClose?: () => void;
  size?: keyof typeof SIZE_CLASS;
  closeDisabled?: boolean;
  closeOnOutsideClick?: boolean;
  role?: 'dialog' | 'alertdialog';
  children: ReactNode;
};

/**
 * Il pannello di dialogo dell'app: titolo, descrizione facoltativa, pulsante Chiudi e il
 * contenuto in `ModalBody` e `ModalFooter` (dentro `ModalForm` quando è un modulo).
 * `closeDisabled` blocca ogni chiusura durante un salvataggio; senza `onClose` il dialogo
 * si chiude solo dalle sue azioni.
 */
export default function Modal({
  title,
  description,
  onClose,
  size = 'md',
  closeDisabled = false,
  closeOnOutsideClick,
  role = 'dialog',
  children,
}: ModalProps) {
  return (
    <Dialog
      onClose={closeDisabled ? undefined : onClose}
      closeOnOutsideClick={closeOnOutsideClick}
      role={role}
      className={`surface-overlay flex max-h-[90vh] w-full flex-col overflow-hidden rounded-xl ${SIZE_CLASS[size]}`}
    >
      <div className="flex shrink-0 items-start justify-between gap-4 border-b border-subtle px-5 py-4">
        <div className="min-w-0">
          <DialogTitle className="text-section-title text-strong">{title}</DialogTitle>
          {description ? (
            <DialogDescription className="mt-1 text-sm text-muted">{description}</DialogDescription>
          ) : null}
        </div>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            disabled={closeDisabled}
            aria-label="Chiudi"
            className={`${iconButtonClass} hover:bg-muted hover:text-strong`}
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        ) : null}
      </div>
      {children}
    </Dialog>
  );
}

/** Il contenuto del dialogo: scorre da solo, così titolo e pulsanti restano visibili. */
export function ModalBody({ children }: { children: ReactNode }) {
  return <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5">{children}</div>;
}

/** Le azioni del dialogo, in fondo e a destra; l'azione principale per ultima. */
export function ModalFooter({ children }: { children: ReactNode }) {
  return (
    <div className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-subtle px-5 py-4">
      {children}
    </div>
  );
}

/** Un modulo che abbraccia `ModalBody` e `ModalFooter`, così i pulsanti lo inviano. */
export function ModalForm({ children, ...props }: ComponentProps<'form'>) {
  return (
    <form className="flex min-h-0 flex-1 flex-col" {...props}>
      {children}
    </form>
  );
}
